import { useMemo } from 'react';
import { useStoreState } from '@discover-data/stores/restaurant/hooks';
import useDate from '@discover-data/stores/hooks/date/useDate';
import useDiscountOnly from '@discover-data/stores/hooks/discountOnly/useDiscountOnly';
import usePeople from '@discover-data/stores/hooks/people/usePeople';
import usePeopleOptions from '@discover-data/stores/hooks/people/usePeopleOptions';
import {
  getPeopleOptionsLabel,
  type PeopleOption,
} from '@discover-data/stores/hooks/people/options';
import { cn } from '@discover-lib/utils';
import useAvailabilityDaysConfig from '@/components/hooks/useAvailabilityDaysConfig';

export type EmptyMessageSubtextProps = {
  defaultMessage?: string;
  className?: string;
  peopleOptions?: PeopleOption[] | null;
  minDiners?: PeopleOption | null;
  maxDiners?: PeopleOption | null;
};

export default function EmptyMessageSubtext({
  defaultMessage = 'Try removing some filters to see more results.',
  className,
  peopleOptions: configuredPeopleOptions,
  minDiners,
  maxDiners,
}: EmptyMessageSubtextProps) {
  const [people] = usePeople(configuredPeopleOptions, minDiners, maxDiners);
  const peopleOptions = usePeopleOptions(configuredPeopleOptions, minDiners, maxDiners);
  const [discountOnly] = useDiscountOnly();
  const [date] = useDate();
  const datesToShow = useStoreState((state) => state.discountDatesToShow);
  const { daysToShow } = useAvailabilityDaysConfig();

  const subText = useMemo(() => {
    if (discountOnly && !peopleOptions.includes(people)) {
      return `50% off deals are only available for ${getPeopleOptionsLabel(peopleOptions)}`;
    }

    if (discountOnly && date !== 'week' && !datesToShow.includes(date)) {
      return `50% off deals are only available for bookings within the next ${daysToShow} days`;
    }

    return defaultMessage;
  }, [people, peopleOptions, discountOnly, date, datesToShow, daysToShow, defaultMessage]);

  return <span className={cn('text-gray', className)}>{subText}</span>;
}
